import _ from "lodash";

import { formatNumber } from "utils";

import { Coordinates, DocCrop, DocFieldRaw, FarmFieldRaw, Statuses } from "models";

export type FieldMatchStatus = "auto_matched" | "need_review";

export type FieldMatchRowData = {
  label: string;
  klimValue: string;
  documentValue: string;
  isMatching: boolean;
};

export const STATUS_LABEL_MAP: Record<FieldMatchStatus, string> = {
  auto_matched: "status.autoMatched",
  need_review: "status.needsReview",
};

export const STATUS_COLOR_MAP: Record<FieldMatchStatus, string> = {
  auto_matched: "green",
  need_review: "orange",
};

// PL doc fields grouped under one platform field carry no boundary of their own; their parcels do.
const isGroupedDocField = (docField: DocFieldRaw | null | undefined): boolean => !!docField?.grouped_doc_fields?.length;

/** Every document boundary to draw for a doc field: one per parcel for a group, else its own. */
export const getDocFieldBoundaries = (docField: DocFieldRaw | null | undefined): Coordinates[][] => {
  const fields = isGroupedDocField(docField) ? docField?.grouped_doc_fields ?? [] : [docField];
  return fields.map((field) => field?.geometry?.boundary_wgs84 ?? []).filter((boundary) => boundary.length > 0);
};

/**
 * Crops of a doc field. A group concatenates its parcels' crops, so the same crop repeats once
 * per parcel; it is kept once per crop code.
 */
export const getDocFieldCrops = (docField: DocFieldRaw | null | undefined): DocCrop[] => {
  const crops = docField?.crops ?? [];
  if (!isGroupedDocField(docField)) return crops;
  return _.uniqBy(crops, (crop) => crop.crop_code ?? crop.crop_name);
};

/**
 * A scalar attribute (name, number, flik, size, ...) of a doc field. A group has no such value of
 * its own, so this reads it off each parcel instead and joins them with ", ".
 */
export const getDocFieldValues = (docField: DocFieldRaw | null | undefined, pick: (field: DocFieldRaw) => string | null | undefined): string[] => {
  const fields = isGroupedDocField(docField) ? docField?.grouped_doc_fields ?? [] : docField ? [docField] : [];
  return fields.map(pick).filter((value): value is string => !!value);
};

/** True for a group whose parcels declare different crops: it must be split before crops can be saved. */
export const requiresDocFieldSplit = (docField: DocFieldRaw | null | undefined): boolean => isGroupedDocField(docField) && !!docField?.is_pre_split_parent;

export const parseLocation = (location: string): Coordinates | undefined => {
  try {
    return JSON.parse(location) as Coordinates;
  } catch {
    return undefined;
  }
};

export const deriveMatchStatus = (record: { overallStatus: string | null }): FieldMatchStatus => {
  if (record.overallStatus === "match") return "auto_matched";
  return "need_review";
};

export const isStatusMatching = (status?: string) => status === "match";

export const buildRows = (dbField: FarmFieldRaw | null, docField: DocFieldRaw | null, statuses: Statuses | null): FieldMatchRowData[] => [
  {
    label: "name",
    klimValue: dbField?.name ?? "-",
    documentValue: getDocFieldValues(docField, (f) => f.field_name).join(", ") || "-",
    isMatching: isStatusMatching(statuses?.field_name_status),
  },
  {
    label: "field",
    klimValue: dbField?.number ?? "-",
    documentValue: getDocFieldValues(docField, (f) => f.field_number).join(", ") || "-",
    isMatching: isStatusMatching(statuses?.field_number_status),
  },
  {
    label: "flik",
    klimValue: dbField?.flik ?? "-",
    documentValue: getDocFieldValues(docField, (f) => f.flik_number).join(", ") || "-",
    isMatching: isStatusMatching(statuses?.flik_status),
  },
  {
    label: "size",
    klimValue: dbField?.size !== undefined ? `${formatNumber({ number: dbField.size })} ha` : "-",
    documentValue:
      getDocFieldValues(docField, (f) => f.field_size)
        .map((size) => `${size} ha`)
        .join(", ") || "-",
    isMatching: isStatusMatching(statuses?.field_size_status),
  },
  {
    label: "mainCrop",
    klimValue: dbField?.crops?.map((c) => c.crop_name).join(", ") || "-",
    documentValue:
      getDocFieldCrops(docField)
        .map((c) => c.crop_name)
        .join(", ") || "-",
    isMatching: isStatusMatching(statuses?.crops_status),
  },
  {
    label: "cropCode",
    klimValue:
      dbField?.crops
        ?.map((c) => c.crop_code)
        .filter((code) => code != null)
        .join(", ") || "-",
    documentValue:
      getDocFieldCrops(docField)
        .map((c) => c.crop_code)
        .filter((code) => code != null)
        .join(", ") || "-",
    isMatching: isStatusMatching(statuses?.crops_status),
  },
];
